import { COMMA, ENTER } from "@angular/cdk/keycodes";

export const separatorKeysCodes: number[] = [ENTER, COMMA];

// Option is a flat structure, from which a hierarchical structure of NestedOption can be built
// It is expected that the id is unique
export interface Option {
  id: number;
  name: string;
  alternateName?: string;
  categoryId?: number | null;
  categoryName?: string | null
}

export interface WeakOption {
  id: number | null;
  name: string;
  alternateName?: string;
  icons?: string[] | null;
  categoryId?: number | null;
  categoryName?: string | null
}

export interface AttributeIconPair<T> {
  attributeName: keyof T;
  attributeIcon: string;
}


// NestedOption is a hierarchical structure with parent (category) and children nodes
// for leaf nodes, value corresponds with id, label with name, isGroup is false and children is null | undefined | []
// for categories, values corresponds to name (to distinguish with leaf nodes and avoid duplicates), 
//    label to name, isGroup is true and children contains the list of child options
export interface NestedOption {
  value: string; // this is the id
  label: string;
  alternateLabel?: string; // this is used as a secondary filter 
  icons?: string[] | null;
  isGroup: boolean;
  children?: NestedOption[];
}


export function enumToSortedNestedOptions<E extends object>(enumObject: E): { value: string; label: string; isGroup: boolean }[] {
  return Object.entries(enumObject)
    .filter(([key, value]) => Number.isNaN(Number(key))) // keep only named keys to avoid duplicates if numeric enum
    .map(([key, value]) => (
      {
        value: key,
        label: value ?? key,
        isGroup: false,
        children: []
      }
    ))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function enumToNestedOptions<E extends object>(enumObject: E): { value: string; label: string; isGroup: boolean }[] {
  return Object.entries(enumObject)
    .filter(([key, value]) => Number.isNaN(Number(key))) // keep only named keys to avoid duplicates if numeric enum
    .map(([key, value]) => (
      {
        value: key,
        label: value ?? key,
        isGroup: false,
        children: []
      }
    ));
}

export function mapStringArrayToSortedNestedOptions(items: string[]): NestedOption[] {
  if (items) {
    return items.map((person, index) => ({
      label: person,
      value: String(index + 1),
      isGroup: false,
      children: []
    })).sort((a, b) => a.label.localeCompare(b.label))
  } else {
    return []
  }
}

export function mapStringArrayToNestedOptions(items: string[]): NestedOption[] {
  if (items) {
    return items.map((person, index) => ({
      label: person,
      value: String(index + 1),
      isGroup: false,
      children: []
    }))
  } else {
    return []
  }
}

/**
 * 
 * @param items 
 * @param sort 
 * @returns 
 */
export function mapToNestedOptions<T extends WeakOption, K extends keyof T>(items: T[], sort: boolean,
  additionalAttributeKey?: K, icons?: AttributeIconPair<T>[]): NestedOption[] {
  const topLevel: NestedOption[] = [];
  const categoryMap = new Map<string, { value: string; label: string; isGroup: boolean, items: NestedOption[] }>();

  for (const item of items) {
    if (item.id !== null) {
      const option: NestedOption = {
        value: String(item.id),
        label: item.name,
        isGroup: false,
        children: []
      };

      if (additionalAttributeKey && additionalAttributeKey in item) {
        const extracted = String(item[additionalAttributeKey]);
        if (extracted)
          option.alternateLabel = extracted;
      }
      if (icons) {
        for (const p of icons) {
          // check if object has attribute with given name that resolves to true
          if (p.attributeName && p.attributeName in item) {
            const attributeValue = item[p.attributeName];
            if (attributeValue) {
              if (option.icons)
                option.icons.push(p.attributeIcon);
              else {
                option.icons = [];
                option.icons.push(p.attributeIcon);
              }
            }
          }
        }
      }

      // Use the categoryName as value and map key to avoid clash with the item number
      if (item.categoryId && item.categoryName) {
        if (!categoryMap.has(item.categoryName)) {
          categoryMap.set(item.categoryName, {
            value: item.categoryName,
            label: item.categoryName,
            isGroup: true,
            items: [],
          });
        }
        categoryMap.get(item.categoryName)!.items.push(option);
      } else {
        topLevel.push(option);
      }
    }
  }

  if (sort) {
    // Sort top-level items
    topLevel.sort((a, b) => a.label.localeCompare(b.label));
    // Sort categories and their children    
    const nestedCategories: NestedOption[] = Array.from(categoryMap.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(group => ({
        value: group.value,
        label: group.label,
        isGroup: group.isGroup,
        children: Array.from(group.items).sort((a, b) => a.label.localeCompare(b.label)), // sort children immutably
      }));
    return [...topLevel, ...nestedCategories].sort((a, b) => a.label.localeCompare(b.label));
  } else {
    const nestedCategories: NestedOption[] = Array.from(categoryMap.values())
      // .sort((a, b) => a.name.localeCompare(b.name))
      .map(group => ({
        value: group.value,
        label: group.label,
        isGroup: false,
        children: group.items,
      }));
    return [...topLevel, ...nestedCategories];
  }
}